import { yupResolver } from '@hookform/resolvers/yup'
import NotificationActions from 'actions/Notification'
import SectorActions from 'actions/Sector'
import { featureFlags } from 'config/featureFlags'
import { ArchiveNotificationModal } from 'components/Modals/ArchiveNotification'
import { ArchiveWarning } from 'components/Modals/ArchiveWarning'
import { AssumeTreatmentModal } from 'components/Modals/AssumeTreatment'
import { CanEditWarning } from 'components/Modals/CanEditWarning'
import { FinishNotificationModal } from 'components/Modals/FinishNotification'
import { ForwardNotificationModal } from 'components/Modals/ForwardNotification'
import { PendingStepModal } from 'components/Modals/PendingStepModal'
import { ProtocolInfoOk } from 'components/Modals/ProtocolInfoOk'
import { ReopenNotificationModal } from 'components/Modals/ReopenNotification'
import { listOfRoles } from 'components/WithRoleAuthorization/WithRoleAuthorization'
import { ROUTES_NAMES } from 'constants/name.routes'
import { notificationStatus } from 'constants/notificationStatus'
import { calculateObjectProbabilityOcurrence } from 'pages/NotificationTreatment/helpers'
import NotificationTreatmentHeader from 'pages/NotificationTreatment/NotificationsTreatmentsSteps/Header'
import { NotificationTreatmentProgressBarNav } from 'pages/NotificationTreatment/ProgressBarNav'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import NotificationService from 'services/Notification'
import { AuthenticationReducer } from 'types/autentication'
import { RootReducerState } from 'types/global'
import { EnumNotificationProcessRequiredStepsRulesStatus, NotificationReducer, NotificationType } from 'types/notification'
import {
    damage_level,
    isNotifiableReopenBlocked,
    isSevereOrDeathDamage,
    isSimplifiedTreatment,
    NOTIFIABLE_REOPEN_BLOCKED_MESSAGE
} from 'utils/damage'
import {
    isLocationReopenBlocked,
    LOCATION_REOPEN_BLOCKED_MESSAGE
} from 'utils/location'
import { isGraveObitoOrHasImageRisk } from 'utils/notifyEvents'
import { notifyUpdateQASchema, notifyUpdateSchema, notifyUpdateSchemaForOlderNotifications } from 'validator/notify_complementary_infos'
import { objectNotificationUpdate } from './helpers'
import AssumeTreatmentInfo from './NotificationsTreatmentsSteps/components/AssumeTreatmentInfo'
import CanNotEditInfo from './NotificationsTreatmentsSteps/components/CanNotEditTreatmentInfo'
import { canFinish, canForward } from './service'
import { NotifyContainer, NotifyContent, NotifyMain } from './styles'
import NotificationsTreatmentsSteps from './NotificationsTreatmentsSteps/notificationsTreatmentsSteps'
import GroupNotificationTreatmentsSteps from './GroupTreatmentsSteps/groupNotificationTreatmentsSteps'

export interface isFinishedStatus {
    label: string
    isDone: boolean
    isRequired: boolean
    isOptional: boolean
    isDispensable: boolean
    currentStepTreatment: number
    isOlder?: boolean
}

const NotificationTreatment = () => {
    const ishikawaRequiredDamages = damage_level.filter(({ value }) => value === 'sem-dano' || value === 'leve' || value === 'moderado')
    const location = useLocation()
    const { currentStep, messageSuccess } = location.state || {}
    const navigate = useNavigate()
    const { id } = useParams()
    const [currentStepSelected, setCurrentStepSelected] = useState(1)
    const [notificationById, setNotificationById] = useState<NotificationType>()
    const [archiveWarningModal, setArchiveWarningModal] = useState(false)
    const [archiveModal, setArchiveModal] = useState(false)
    const [forwardModal, setForwardModal] = useState(false)
    const [finishModal, setFinishModal] = useState(false)
    const [pendingStepModal, setPendingStepModal] = useState(false)
    const [assumeTreamentModal, setAssumeTreamentModal] = useState(false)
    const [reopenNotificationModal, setReopenNotificationModal] = useState(false)
    const [editWarningModal, setEditWarningModal] = useState(false)
    const [statusEdit, setStatusEdit] = useState(false)
    const [isMissingForwardToFinish, setIsMissingForwardToFinish] = useState(false)
    const [isMissingAnvisaNumber, setIsMissingAnvisaNumber] = useState(false)
    const [tooltipAlert, setTooltipAlert] = useState('')
    const [forwordErrorMessage, setForwordErrorMessage] = useState('')
    const [finishErrorMessage, setFinishErrorMessage] = useState('')
    const [notificationStatusState, setNotificationStatusState] = useState('')
    const [finishedSteps, setFinishedSteps] = useState<isFinishedStatus[]>([])
    const [pendingModalText, setPendingModalText] = useState({ subtitle: '' })
    const [finishModalData, setFinishModalData] = useState<{
        submitButtonText: string
        title: string
        subtitle: string
        type: 'forward' | 'finish'
    }>({
        submitButtonText: '',
        title: '',
        subtitle: '',
        type: 'finish'
    })
    const [disclosure, setDisclosure] = useState({
        u_disclosure_comments: '',
        u_disclosure: 'u_disclosure_yes',
        errors: null
    })

    const [disclosureError, setDisclosureError] = useState('')
    const [managersReviewError, setManagersReviewError] = useState('')
    const [showDisclosure, setShowDisclosure] = useState(false)
    // Evita loops de auto-ajuste em caso de erro de API (ex.: 405)
    const autoFixAttemptRef = useRef<{
        notificationId: string | undefined
        ishikawa: boolean
        london: boolean
        accountability: boolean
        actionPlan: boolean
    }>({
        notificationId: undefined,
        ishikawa: false,
        london: false,
        accountability: false,
        actionPlan: false
    })

    const notificationDispatch = useDispatch()
    const sectorDispatch = useDispatch()
    const notificationSelector: NotificationReducer = useSelector(
        (state: RootReducerState) => state.notificationsReducer
    )
    const notificationSelectorIsLoading = useSelector(
        (state: RootReducerState) => state.notificationsReducer.loading
    )

    const londonProtocol = useMemo(
        () => notificationSelector.londonProtocol,
        [notificationSelector.londonProtocol]
    )

    const accountabilityMatrixMemo = useMemo(() => {
        if (notificationSelector?.accountabilityMatrix) {
            return notificationSelector?.accountabilityMatrix[0]
        }
    }, [notificationSelector?.accountabilityMatrix])

    const ishikawa = useMemo(
        () => notificationSelector.ishikawaDiagram,
        [notificationSelector.ishikawaDiagram]
    )
    const actionPlans = useMemo(
        () => notificationSelector.actionPlans,
        [notificationSelector.actionPlans]
    )
    const authenticationSelector: AuthenticationReducer = useSelector(
        (state: RootReducerState) => state.authenticationReducers
    )

    const loggedInUserIsResponsible = useMemo(() => {
        return authenticationSelector?.dataJwt?.sys_id === notificationSelector?.notificationById?.u_assigned_to?.value
    }, [authenticationSelector?.dataJwt?.sys_id, notificationSelector?.notificationById?.u_assigned_to?.value])

    useEffect(() => {
        let damagesLevelClosureIds = damage_level
            .filter((damage) => ['leve', 'moderado', 'grave', 'obito'].includes(damage.value))
            .map((damage) => damage.id);

        if (!!(notificationById?.damage_level && notificationById?.u_image_risk)) {
            const shouldShow =
                damagesLevelClosureIds.includes(notificationById?.damage_level?.value) ||
                notificationById?.u_image_risk.value !== 'u_image_risk_no' ||
                notificationById?.u_never_events_or_sentinel_event?.value === 'yes';

            if (!['6'].includes(notificationById?.u_state?.value)) {
                setShowDisclosure(shouldShow);
            }
        }
    }, [notificationById]);

    useEffect(() => {
        //@TODO: US-66215 - Optimização de multiplos requests
        //NotificationActions.getNotificationById(notificationDispatch, id)
        SectorActions.getAllSectorsGroupedByLocationsId(sectorDispatch)
    }, [])

    // Reset dos guards quando muda a notificação
    useEffect(() => {
        autoFixAttemptRef.current = {
            notificationId: id,
            ishikawa: false,
            london: false,
            accountability: false,
            actionPlan: false
        }
    }, [id])

    /**
     * Evento usando para contraolar o redirecionamento para a aba de tratamento
     */
    useEffect(() => {
        if (currentStep)
            setCurrentStepSelected(currentStep)
    }, [currentStep])
    /**
     * Exbime tooltip de sucesso
     */
    useEffect(() => {
        if (messageSuccess) {
            setTooltipAlert(messageSuccess)
            setTimeout(() => {
                setTooltipAlert('')
            }, 3000)
        }
    }, [messageSuccess])

    useEffect(() => {
        let notification = notificationSelector?.notificationById
        let notificationProcessRequiredStepsRules = calculateObjectProbabilityOcurrence(notification)
        NotificationActions.defineObjectNotificationProcessRequiredStepsRules(notificationDispatch, notificationProcessRequiredStepsRules)
        if (notification?.u_state) {
            setNotificationStatusState(notification?.u_state.display_value)
        }
        setNotificationById(notification)
        const formData = getValues()
        reset(formData) //clean form dirty
    }, [notificationSelector.notificationById?.u_state, notificationById?.sys_id])


    //fetch currentStepSelected data for finish
    useEffect(() => {
        NotificationActions.getIshikawaByNotificationId(notificationDispatch, id)
        NotificationActions.getLondonProtocolByNotificationId(notificationDispatch, id)
        NotificationActions.getAccountabilityMatrixByNotificationId(notificationDispatch, id)
        NotificationActions.getListOfActionPlan(notificationDispatch, id)
    }, [id])

    //empty erro disclosure
    useEffect(() => {
        setDisclosureError('')
    }, [disclosure])

    const removeIshikawaSelecion = async () => {
        const ishikawa = notificationSelector?.ishikawaDiagram
        if (ishikawa?.sys_id) {
            let IshikawaSN: any = {
                sys_id: ishikawa?.sys_id,
                do_not_perform_optional: 'false',
            }
            await NotificationService.saveIshikawaDiagram(IshikawaSN)
            NotificationActions.getIshikawaByNotificationId(notificationDispatch, id)
        }
    }

    const removeAccountabilityMatrixSelecion = async () => {
        const accountabilityMatrix = notificationSelector?.accountabilityMatrix
        if (accountabilityMatrix[0]?.sys_id) {
            let AccountabilityMatrixSN: any = {
                sys_id: accountabilityMatrixMemo?.sys_id,
                u_do_not_perform_optional: 'false',
            }
            await NotificationService.saveAccountabilityMatrix(AccountabilityMatrixSN)
            NotificationActions.getAccountabilityMatrixByNotificationId(notificationDispatch, id)
        }
    }

    const removeLondonProtocolSelecion = async () => {
        const londonProtocolLocal = notificationSelector?.londonProtocol
        if (londonProtocolLocal?.sys_id) {
            let LondonProtocolSN: any = {
                sys_id: londonProtocol?.sys_id,
                do_not_perform_optional: 'false',
            }
            await NotificationService.saveLondonProtocol(LondonProtocolSN, notificationSelector.notificationById)
            NotificationActions.getLondonProtocolByNotificationId(notificationDispatch, id)
        }
    }

    const removeActionPlanSelecion = async () => {
        if (autoFixAttemptRef.current.actionPlan) {
            return
        }

        const actionsLocal = notificationSelector?.actionPlans || []
        const records = actionsLocal
            .filter((row) => row?.sys_id)
            .map((row) => ({
                sys_id: row.sys_id,
                hospital_incident: id,
                u_do_not_perform_optional: 'false',
                how: row.how || '',
                how_much: row.how_much || '',
                what: row.what || '',
                when: row.when || '',
                where: row.where || '',
                who: row.who || '',
                why: row.why || '',
                u_notifica_user: authenticationSelector?.dataJwt?.sys_id || ''
            }))

        if (records.length === 0) {
            return
        }

        autoFixAttemptRef.current.actionPlan = true
        try {
            await NotificationActions.updateActionPlanBatch(notificationDispatch, {
                records,
                options: {
                    stopOnError: false
                }
            })
            NotificationActions.getListOfActionPlan(notificationDispatch, id)
        } catch (e) {
            console.error('Erro ao atualizar u_do_not_perform_optional do plano de ação:', e)
        }
    }

    useEffect(() => {
        const isOlder = notificationById?.u_state.value != `Nova` && notificationById?.u_probability_occurence?.value === ''
        /* ISHIKAWA */
        const ishikawaRequired = ishikawaRequiredDamages?.find(({ id }) => id === notificationSelector?.notificationById?.damage_level?.value)
        const isImageRisk = notificationById?.u_image_risk?.value === 'u_image_risk_yes'
        const stateIshikawa = notificationSelector?.notificationProcessRequiredStepsRules?.ishikawaMandatory
        if ((isOlder ? (ishikawaRequired !== undefined && !isImageRisk) : stateIshikawa == EnumNotificationProcessRequiredStepsRulesStatus.Required)
            && ishikawa?.do_not_perform_optional == 'true'
            && !autoFixAttemptRef.current.ishikawa) {
            autoFixAttemptRef.current.ishikawa = true
            removeIshikawaSelecion()
        }
        /* LONDON PROTOCOL */
        const damageLevelValue = notificationSelector?.notificationById?.damage_level?.value
        const isGraveObitoOrHasImageRiskRequired = isGraveObitoOrHasImageRisk({
            damageLevelValue: damageLevelValue,
            imageRiskValue: notificationById?.u_image_risk?.value
        })
        const stateLondonProtocol = notificationSelector?.notificationProcessRequiredStepsRules?.londonProtocolMandatory
        if ((isOlder ? isGraveObitoOrHasImageRiskRequired : stateLondonProtocol == EnumNotificationProcessRequiredStepsRulesStatus.Required)
            && londonProtocol?.do_not_perform_optional == 'true'
            && !autoFixAttemptRef.current.london) {
            autoFixAttemptRef.current.london = true
            removeLondonProtocolSelecion()
        }
        /* ACCOUNT MATRIX */
        const simplifiedDamages = damage_level.filter(({ value }) => value === 'near-miss' || value === 'notifiable' || value === 'sem-dano')
        const simplifiedElement = simplifiedDamages.find(({ id }) => id === damageLevelValue)
        const checkboxRequired = !isImageRisk && simplifiedElement !== undefined
        const stateAccountMatrix = notificationSelector?.notificationProcessRequiredStepsRules?.accountabilityMatrixMandatory
        if ((isOlder ? !checkboxRequired : stateAccountMatrix == EnumNotificationProcessRequiredStepsRulesStatus.Required)
            && accountabilityMatrixMemo?.u_do_not_perform_optional == 'true'
            && !autoFixAttemptRef.current.accountability) {
            autoFixAttemptRef.current.accountability = true
            removeAccountabilityMatrixSelecion()
        }
        /* ACTION PLAN */
        const optionalActionPlanDamages = damage_level.filter(({ value }) => value === 'near-miss' || value === 'notifiable')
        const isRequiredActionPlan = optionalActionPlanDamages.find(item => item.id === damageLevelValue) && !isImageRisk
        const stateActionPlan = notificationSelector?.notificationProcessRequiredStepsRules?.actionPlanMandatory
        if ((isOlder ? !isRequiredActionPlan : stateActionPlan == EnumNotificationProcessRequiredStepsRulesStatus.Required)
            && (actionPlans && actionPlans?.length == 1 && actionPlans[0]?.u_do_not_perform_optional == 'true')
            && !autoFixAttemptRef.current.actionPlan) {
            removeActionPlanSelecion()
        }
    }, [notificationSelector?.notificationProcessRequiredStepsRules])

    useEffect(() => {
        let causes = ishikawa?.categories
            .map((category) => category.causes)
            .map((itens) => itens.toString())
            .filter((i) => i.length)

        const ishikawaIsChecked = ishikawa?.do_not_perform_optional === 'true'
        const ishikawaIsDone = ishikawaIsChecked || causes?.length > 0

        const isOlder = notificationById?.u_state.value != `Nova` && notificationById?.u_probability_occurence?.value === ''
        const isEventSentinelOrneverEvents = notificationById?.u_never_events_or_sentinel_event?.value == 'yes'

        const stateIshikawa = notificationSelector?.notificationProcessRequiredStepsRules?.ishikawaMandatory
        const stateLondonProtocol = notificationSelector?.notificationProcessRequiredStepsRules?.londonProtocolMandatory
        const stateAccountMatrix = notificationSelector?.notificationProcessRequiredStepsRules?.accountabilityMatrixMandatory
        const stateActionPlan = notificationSelector?.notificationProcessRequiredStepsRules?.actionPlanMandatory

        const ishikawaRequired = ishikawaRequiredDamages?.find(({ id }) => id === notificationSelector?.notificationById?.damage_level?.value)
        const isImageRisk = notificationById?.u_image_risk?.value === 'u_image_risk_yes'

        const ishikawaStatus: isFinishedStatus = {
            label: 'Análise de Ishikawa',
            isDone: ishikawaIsDone,
            isDispensable: stateIshikawa === `Dispensable`,
            isRequired: isOlder ? (ishikawaRequired !== undefined && !isImageRisk) : stateIshikawa === `Required`,
            isOptional: stateIshikawa === `Optional` || isEventSentinelOrneverEvents,
            currentStepTreatment: 2,
            isOlder: isOlder
        }

        //london finish feedback
        const londonIsDone =
            !!londonProtocol?.date_of_patient_admission ||
            londonProtocol?.do_not_perform_optional === 'true'

        const damageLevelValue = notificationSelector?.notificationById?.damage_level?.value
        const isGraveObitoOrHasImageRiskRequired = isGraveObitoOrHasImageRisk({
            damageLevelValue: damageLevelValue,
            imageRiskValue: notificationById?.u_image_risk?.value
        })

        const londonStatus: isFinishedStatus = {
            label: 'Protocolo de Londres',
            isDone: londonIsDone,
            isDispensable: stateLondonProtocol === `Dispensable`,
            isRequired: isEventSentinelOrneverEvents ||
                (isOlder ? (isGraveObitoOrHasImageRiskRequired) : stateLondonProtocol === `Required`),
            isOptional: stateLondonProtocol === `Optional`,
            currentStepTreatment: 3,
            isOlder: isOlder
        }

        //accountabilityMatrix finish feedback
        const matrixIsDone =
            !!accountabilityMatrixMemo &&
            (accountabilityMatrixMemo?.u_do_not_perform_optional === 'true' || accountabilityMatrixMemo?.u_matrix_result !== '')

        const simplifiedDamages = damage_level.filter(({ value }) => value === 'near-miss' || value === 'notifiable' || value === 'sem-dano')
        const simplifiedElement = simplifiedDamages.find(({ id }) => id === damageLevelValue)
        const checkboxRequired = !isImageRisk && simplifiedElement !== undefined

        const accountabilityMatrixStatus: isFinishedStatus = {
            label: 'Matriz de responsabilização',
            isDone: matrixIsDone,
            isDispensable: stateAccountMatrix === `Dispensable`,
            isRequired: isEventSentinelOrneverEvents ||
                (isOlder ? (!checkboxRequired) : stateAccountMatrix === `Required`),
            isOptional: stateAccountMatrix === `Optional`,
            currentStepTreatment: 4,
            isOlder: isOlder
        }

        //action
        const optionalActionPlanDamages = damage_level.filter(({ value }) => value === 'near-miss' || value === 'notifiable')
        const isRequiredActionPlan = optionalActionPlanDamages.find(item => item.id === damageLevelValue) && !isImageRisk

        const actionIsDone = actionPlans?.length > 0 ? (actionPlans.length == 1 ? (actionPlans[0]?.u_do_not_perform_optional == 'true' || actionPlans[0]?.what != "") : true) : false
        const actionStatus: isFinishedStatus = {
            label: 'Plano de ação',
            isDone: actionIsDone,
            isDispensable: stateActionPlan === `Dispensable`,
            isRequired: isEventSentinelOrneverEvents ||
                (isOlder ? !isRequiredActionPlan : stateActionPlan === `Required`),
            isOptional: stateActionPlan === `Optional`,
            currentStepTreatment: 5,
            isOlder: isOlder
        }

        //finish
        const userProfile = authenticationSelector?.dataJwt?.profile
        const isSimplified = isSimplifiedTreatment(
            damageLevelValue,
            isImageRisk,
            userProfile
        )
        let isFinished = notificationById?.u_managers_review?.display_value?.trim() !== ''
        if (showDisclosure) {
            isFinished = ((disclosure.u_disclosure === 'u_disclosure_no') || (disclosure.u_disclosure === 'u_disclosure_yes' && disclosure.u_disclosure_comments.trim() !== ''))
        }
        const finishStatus: isFinishedStatus = {
            label: 'Finalizar notificação',
            isDone: !isSimplified || Boolean(isFinished),
            isRequired: true,
            isOptional: true,
            isDispensable: false,
            currentStepTreatment: 6
        }

        let currentFinishedSteps = []

        if (isEventSentinelOrneverEvents || isOlder || stateIshikawa === `Required` || stateIshikawa === `Optional`)
            currentFinishedSteps.push(ishikawaStatus)

        if (isEventSentinelOrneverEvents || isOlder || stateLondonProtocol === `Required` || stateLondonProtocol === `Optional`)
            currentFinishedSteps.push(londonStatus)

        if (isEventSentinelOrneverEvents || isOlder || stateAccountMatrix === `Required` || stateAccountMatrix === `Optional`)
            currentFinishedSteps.push(accountabilityMatrixStatus)

        if (isEventSentinelOrneverEvents || isOlder || stateActionPlan === `Required` || stateActionPlan === `Optional`)
            currentFinishedSteps.push(actionStatus)

        currentFinishedSteps.push(finishStatus)

        setFinishedSteps(currentFinishedSteps)
    }, [
        londonProtocol,
        ishikawa,
        accountabilityMatrixMemo,
        actionPlans,
        notificationById,
        disclosure
    ])

    const userProfileLocationQA = useMemo(
        () => authenticationSelector?.dataJwt?.profile === listOfRoles?.location_qa,
        [authenticationSelector?.dataJwt]
    )

    const isV2 = useMemo(() => {
        if (notificationById?.u_version?.value?.toLowerCase() === 'v1') {
            return false
        }
        return notificationById?.u_state.value != 'Nova' &&
            notificationById?.hasOwnProperty('u_probability_occurence') &&
            notificationById?.hasOwnProperty('u_never_events_or_sentinel_event');
    }, [notificationById])

    const formResolver = useMemo(() => {
        const profile = authenticationSelector?.dataJwt?.profile;

        // console.log('=== isV2: ', isV2)
        // Para outros perfis, verificar se é notificação antiga
        if (!isV2 && !userProfileLocationQA) {
            return yupResolver(notifyUpdateSchemaForOlderNotifications(false))
        }

        // Para o perfil QA, sempre usar o schema QA (mesmo para notificações antigas)
        if (userProfileLocationQA) {
            return yupResolver(notifyUpdateQASchema(isV2, profile))
        }

        // Para notificações novas de outros perfis, usar schema normal
        return yupResolver(notifyUpdateSchema(true))
    }, [userProfileLocationQA, isV2])

    const {
        register,
        control,
        formState: { errors, dirtyFields, isValid },
        getValues,
        reset,
        watch,
        setValue,
        trigger,
        resetField
    } = useForm({
        resolver: formResolver
    })

    useEffect(() => {
        const formData = getValues()
        reset(formData) //clean form dirty
    }, [currentStepSelected])

    const updateNotificationValues = () => {
        return objectNotificationUpdate({ getValues })
    }

    // Estado para controlar quando devemos fazer scroll para o primeiro erro
    const [shouldScrollToError, setShouldScrollToError] = useState(false)

    // Função para fazer scroll e focar no primeiro campo com erro
    const scrollToFirstError = async (formErrors: any) => {
        if (Object.keys(formErrors).length > 0) {
            const firstErrorField = Object.keys(formErrors)[0];

            // Tentar diferentes seletores para encontrar o campo
            const selectors = [
                `[data-field-container="${firstErrorField}"]`,
                `[name="${firstErrorField}"]`,
                `input[name="${firstErrorField}"]`,
                `select[name="${firstErrorField}"]`,
                `textarea[name="${firstErrorField}"]`,
                `button[name="${firstErrorField}"]`,
                `[data-field="${firstErrorField}"]`,
                `#${firstErrorField}`,
                `[data-testid="test-select-input"][name="${firstErrorField}"]`,
                `div[data-field-container="${firstErrorField}"]`
            ];

            let element = null;

            // Buscar elemento usando os seletores
            for (const selector of selectors) {
                try {
                    element = document.querySelector(selector);
                    if (element) break;
                } catch (error) {
                    // Continuar tentando outros seletores
                }
            }

            // Se não encontrou, tentar busca por proximidade de labels
            if (!element) {
                const labels = document.querySelectorAll('label');
                for (const label of labels) {
                    if (label.textContent?.toLowerCase().includes(firstErrorField.toLowerCase().replace('_', ' '))) {
                        const fieldContainer = label.closest('[data-field-container]') || label.parentElement;
                        if (fieldContainer) {
                            element = fieldContainer;
                            break;
                        }
                    }
                }
            }

            if (element) {
                // Fazer scroll para o elemento
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest'
                });

                // Aguardar o scroll terminar e então aplicar o foco
                setTimeout(() => {
                    // Estratégias de foco em ordem de prioridade
                    const focusStrategies = [
                        // 1. Procurar button dentro do container (para InputSelect)
                        () => {
                            const button = element.querySelector('button[name="' + firstErrorField + '"]');
                            if (button && typeof button.focus === 'function') {
                                button.focus();
                                return true;
                            }
                            return false;
                        },

                        // 2. Procurar input dentro do container
                        () => {
                            const input = element.querySelector('input[name="' + firstErrorField + '"]');
                            if (input && typeof input.focus === 'function') {
                                input.focus();
                                return true;
                            }
                            return false;
                        },

                        // 3. Procurar qualquer elemento focável dentro do container
                        () => {
                            const focusableElements = element.querySelectorAll(
                                'input, select, textarea, button, [tabindex]:not([tabindex="-1"])'
                            );

                            if (focusableElements.length > 0) {
                                const firstFocusable = focusableElements[0] as HTMLElement;
                                if (typeof firstFocusable.focus === 'function') {
                                    firstFocusable.focus();
                                    return true;
                                }
                            }
                            return false;
                        },

                        // 4. Tentar focar no próprio container se for focável
                        () => {
                            if (element instanceof HTMLElement && typeof element.focus === 'function') {
                                element.tabIndex = -1;
                                element.focus();
                                return true;
                            }
                            return false;
                        },

                        // 5. Abrir o select se for um Listbox (HeadlessUI)
                        () => {
                            const listboxButton = element.querySelector('[role="button"]') ||
                                element.querySelector('button');
                            if (listboxButton && typeof listboxButton.click === 'function') {
                                listboxButton.focus();
                                setTimeout(() => {
                                    listboxButton.click();
                                }, 100);
                                return true;
                            }
                            return false;
                        }
                    ];

                    // Tentar cada estratégia até uma funcionar
                    for (const strategy of focusStrategies) {
                        try {
                            if (strategy()) {
                                break;
                            }
                        } catch (error) {
                            // Continuar tentando outras estratégias
                        }
                    }
                }, 800);
            }
        }
    };

    // useEffect que executa quando há mudança nos erros E quando devemos fazer scroll
    useEffect(() => {
        // Scroll para o primeiro erro apenas quando solicitado
        if (shouldScrollToError && Object.keys(errors).length > 0) {
            console.log('Erros do formulário:', errors);
            scrollToFirstError(errors)
            setShouldScrollToError(false)
        }
    }, [errors, shouldScrollToError])

    const editModalWarning = async () => {
        try {
            const isFormValid = await trigger()
            if (isFormValid) {
                setEditWarningModal(true)
            } else {
                // Ativar o flag para fazer scroll quando os erros forem atualizados
                setShouldScrollToError(true)
            }
        } catch (error) {
            // Em caso de erro, tentar fazer scroll se houver erros conhecidos
            if (Object.keys(errors).length > 0) {
                await scrollToFirstError(errors)
            } else {
                setShouldScrollToError(true)
            }
            setEditWarningModal(true)
        }
    }

    // Atualizar o formulário quando o resolver muda
    useEffect(() => {
        const formData = getValues()
        reset(formData)
    }, [formResolver])

    const [urlDeleteS3AttachedFile, setUrlDeleteS3AttachedFile] = useState<string>()
    const [urlUploadS3AttachedFile, setUrlUploadS3AttachedFile] = useState<string>()
    const [uploadFileS3Attached, setFileUploadS3Attached] = useState<File>()

    const canUpdateFile = () => {
        return [listOfRoles.admin, listOfRoles.location_qa, listOfRoles.corporate_quality_board].includes(authenticationSelector?.dataJwt?.profile)
    }
    /**
     * Salva a edição da 
     * A função handleSaveEdit tem várias responsabilidades relacionadas à atualização de uma notificação e manipulação de arquivos
     c 
zz

        Verifica se é possível atualizar o arquivo (canUpdateFile).
        Se houver uma URL para deletar um arquivo S3 (urlDeleteS3AttachedFile), tenta deletar o arquivo usando NotificationService.deleteS3AttachedFile.
        Fechamento do Modal de Aviso de Edição:

        Fecha o modal de aviso de edição (setEditWarningModal(false)).
        Atualização da Notificação:

        Atualiza os valores da notificação (updateNotificationValues).
        Envia a notificação atualizada para o serviço (NotificationService.updateNotification).
        Reset do Formulário:
        Reseta os valores do formulário (reset(getValues())).

        Atualização do Status de Edição:
        Define o status de edição como verdadeiro (setStatusEdit(true)).
        Atualiza a notificação por ID (NotificationActions.getNotificationById).
        Define o status de edição como falso após 3 segundos (setTimeout(() => setStatusEdit(false), 3000)).
        Salvamento do Protocolo de Londres:

        Verifica se a notificação atualizada e o protocolo de Londres atendem a certas condições.
        Se as condições forem atendidas, salva o protocolo de Londres usando NotificationService.saveLondonProtocol.
        Tratamento de Erros:

        Captura e loga qualquer erro que ocorra durante o processo (console.error(err)).
     */

    const handleSaveEdit = async () => {
        try {
            if (canUpdateFile()) {
                if (urlDeleteS3AttachedFile) {
                    try {
                        await NotificationService.deleteS3AttachedFile(urlDeleteS3AttachedFile);
                    } catch (error) {
                        console.error('Ocorreu um erro ao remover o arquivo do S3:', error);
                    }
                }

            }

            setEditWarningModal(false)

            // Atualiza a notificação
            const updatedNotification = updateNotificationValues();
            const result = await NotificationService.updateNotification(updatedNotification, id)

            // Obtém e redefine os valores do formulário
            const formValues = getValues();
            const damageLevelSelected = formValues.damage_level;
            reset(formValues)

            setStatusEdit(true)

            // Atualiza a notificação por ID
            await NotificationActions.getNotificationById(notificationDispatch, id)

            setTimeout(() => setStatusEdit(false), 3000)

            const resultNotification = result?.data?.result

            const changedDamageLevel = damageLevelSelected !== resultNotification.damage_level?.value
            if (changedDamageLevel) {
                console.log("Houve mudança de grau do dano, logo deve atualizar do_not_perform para false das etapas seguintes")
                checkDoNotPerformFalseWhenChangedDamageLevel();
            }

            // Verifica as condições e salva o London Protocol
            const londonProtocol = notificationSelector?.londonProtocol
            if (
                resultNotification &&
                londonProtocol?.sys_id &&
                resultNotification.document_type?.value === 'atendimento'
            ) {
                const careNumber = resultNotification.document_number?.value
                let londonProtocolSN;
                if (changedDamageLevel) {
                    londonProtocolSN = {
                        sys_id: londonProtocol.sys_id,
                        u_patient_care_number: careNumber,
                        u_do_not_perform_optional: 'false',
                    }
                } else {
                    londonProtocolSN = {
                        sys_id: londonProtocol.sys_id,
                        u_patient_care_number: careNumber,
                    }
                }

                await NotificationService.saveLondonProtocol(
                    londonProtocolSN,
                    notificationSelector.notificationById
                )
            }

        } catch (err) {
            console.error(err)

        }
    }

    const checkDoNotPerformFalseWhenChangedDamageLevel = async () => {
        const ishikawa = notificationSelector?.ishikawaDiagram
        if (ishikawa.sys_id) {
            let IshikawaSN: any = {
                ...(ishikawa?.sys_id
                    ? { sys_id: ishikawa?.sys_id }
                    : {}),
                u_do_not_perform_optional: 'false',
            }
            await NotificationService.saveIshikawaDiagram(IshikawaSN)
        }

        const accountabilityMatrix = notificationSelector?.accountabilityMatrix
        if (accountabilityMatrix?.sys_id) {
            let AccountabilityMatrixSN: any = {
                ...(accountabilityMatrixMemo?.sys_id
                    ? { sys_id: accountabilityMatrixMemo?.sys_id }
                    : {}),
                u_do_not_perform_optional: 'false',
            }
            await NotificationService.saveAccountabilityMatrix(AccountabilityMatrixSN)
        }

        if (notificationSelector?.actionPlans?.length > 0) {
            let actionPlanSN: any = {
                u_do_not_perform_optional: 'false',
                u_action_plans: notificationSelector?.actionPlans
            }

        }
    }

    const handleCancel = () => {
        NotificationActions.getNotificationById(notificationDispatch, id)
    }

    useEffect(() => {
        if (notificationSelector.notificationById) {
            setNotificationById(notificationSelector.notificationById)
        }
    }, [id, notificationSelector.notificationById])


    useEffect(() => {
        if (urlDeleteS3AttachedFile) {
            setValue('u_attachments_url', "")
            setValue('u_attachments_size', "")
            setValue('u_attachments_string', "")
        }
    }, [urlDeleteS3AttachedFile])

    useEffect(() => {
        if (urlUploadS3AttachedFile) {
            setValue('u_attachments_url', urlUploadS3AttachedFile)
            setValue('u_attachments_size', uploadFileS3Attached.size.toString())
            setValue('u_attachments_string', uploadFileS3Attached.name)
        }
    }, [urlUploadS3AttachedFile])

    function handleArchiveNotification() {
        setArchiveModal(true)
    }

    function handleReopenNotification() {
        setReopenNotificationModal(true)
    }

    function submitReopenNotification() {
        setReopenNotificationModal(false)

        if (
            isNotifiableReopenBlocked(notificationById?.damage_level?.value) ||
            isLocationReopenBlocked(notificationById?.location_incident)
        ) {
            const blockedMessage = isNotifiableReopenBlocked(notificationById?.damage_level?.value)
                ? NOTIFIABLE_REOPEN_BLOCKED_MESSAGE
                : LOCATION_REOPEN_BLOCKED_MESSAGE
            setTooltipAlert(blockedMessage)
            setTimeout(() => {
                setTooltipAlert('')
            }, 4000)
            return
        }

        NotificationService.reopenNotification(
            id!,
            authenticationSelector?.dataJwt?.mail!,
            notificationStatusState,
            notificationById?.u_sub_state?.value
        )
        .then(async () => {
            setCurrentStepSelected(1)
            setTooltipAlert('Notificação reaberta com sucesso!')
            setTimeout(() => {
                setTooltipAlert('')
            }, 4000)

            await NotificationActions.getNotificationById(notificationDispatch, id)
            NotificationActions.getIshikawaByNotificationId(notificationDispatch, id)
            NotificationActions.getLondonProtocolByNotificationId(notificationDispatch, id)
            NotificationActions.getAccountabilityMatrixByNotificationId(notificationDispatch, id)
            NotificationActions.getListOfActionPlan(notificationDispatch, id)
        })
        .catch((error) => {
            console.error('Erro ao reabrir notificação:', error)
            setTooltipAlert('Erro ao reabrir notificação. Tente novamente.')
            setTimeout(() => {
                setTooltipAlert('')
            }, 3000)
        })
    }

    function submitArchiveWarning() {
        setArchiveWarningModal(false)
        setArchiveModal(true)
    }
    function handleForwardNotification() {
        if (
            !canForward(
                notificationSelector?.notificationById,
                setForwordErrorMessage
            )
        ) {
            return
        }
        // check if can forward
        NotificationService.encaminharNotification(
            notificationSelector.notificationById?.sys_id?.value,
            authenticationSelector?.dataJwt?.name
        ).then(() => {
            navigate(`${ROUTES_NAMES.notifications}`, { state: { encaminhado: true } })
        })
            .catch((error) => {
                console.log(error)
            })
    }
    /**
     * Realiza o realiza o redirecionamento para aba obrigatória que não foi preenchida
     */
    let handlePendingModal = () => {
        const missingStepObj = finishedSteps.find((step) => !step.isDone)
        if (missingStepObj) {
            setCurrentStepSelected(missingStepObj.currentStepTreatment)
        }

        setPendingStepModal(false)
    }

    function handleOpenForwardNotification() {
        setForwardModal(true)
    }

    /**
     *
     * @param notificationObj Realizar o auto save dos campos de disclosure
     */
    function disclosureFieldsAutoSave(notificationObj: any) {
        const { u_disclosure_comments, u_disclosure } = notificationObj

        let notificationById = { ...notificationSelector.notificationById }
        let updateNotification = { u_disclosure_comments, u_disclosure }

        if (u_disclosure_comments || u_disclosure_comments === '') {
            notificationById.u_disclosure_comments = {
                display_value: u_disclosure_comments,
                value: u_disclosure_comments
            }
            updateNotification = {
                ...updateNotification,
                u_disclosure_comments: u_disclosure_comments
            }
        }

        if (u_disclosure) {
            notificationById.u_disclosure = {
                display_value: u_disclosure,
                value: u_disclosure
            }
            updateNotification = {
                ...updateNotification,
                u_disclosure: u_disclosure
            }
        }

        NotificationService.updateNotificationGeneric(
            updateNotification,
            notificationSelector.notificationById?.sys_id?.value
        ).then((response: any) => {
            NotificationActions.setCurrentNotificationById(
                notificationDispatch,
                notificationById
            )
        })
    }

    function handleFinishNotification({
        type
    }: {
        type: 'forward' | 'finish'
    }) {
        // Primeiro, vamos validar o formulário se estivermos no step 1
        const validateFormAndContinue = async () => {
            // Se estivermos no step 1, validar o formulário primeiro
            if (currentStepSelected === 1) {
                try {
                    const isFormValid = await trigger()
                    if (!isFormValid) {
                        // Se formulário inválido, fazer scroll para o primeiro erro e parar
                        setShouldScrollToError(true)
                        return false
                    }
                } catch (error) {
                    // Em caso de erro, tentar fazer scroll se houver erros conhecidos
                    if (Object.keys(errors).length > 0) {
                        await scrollToFirstError(errors)
                    } else {
                        setShouldScrollToError(true)
                    }
                    return false
                }
            }
            return true
        }

        const proceedWithFinish = () => {
            const notDoneOlder = finishedSteps.find((checkStatus) => !checkStatus.isDone)
            const notDoneNew = finishedSteps.find((checkStatus) => !checkStatus.isDone && (checkStatus.isRequired || checkStatus.isOptional))

            const isOlder = notificationSelector.notificationById?.u_state.value != `Nova` && notificationSelector.notificationById?.u_probability_occurence?.value === ''
            notificationSelector.londonProtocol

            if (type === 'finish') {
                if (
                    disclosure.u_disclosure === 'u_disclosure_yes' &&
                    (disclosure.u_disclosure_comments === undefined ||
                        disclosure.u_disclosure_comments === null ||
                        disclosure.u_disclosure_comments.trim() === '')
                ) {
                    setDisclosureError('Preenchimento obrigatório.')
                    return
                }
                setPendingModalText({
                    subtitle: `Para finalizar uma notificação,
                certifique-se de que todos os dados
                obrigatórios foram preenchidos.`
                })
                setFinishModalData({
                    title: 'Finalizar notificação',
                    subtitle: `Ao finalizar uma notificação, não
                    será possível editar as informações
                    de seu tratamento.`,
                    submitButtonText: 'Finalizar notificação',
                    type: 'finish'
                })
                if (isOlder ? notDoneOlder : (notDoneNew != undefined)) {
                    setPendingStepModal(true)
                } else {
                    const notificationById = notificationSelector?.notificationById
                    const incidenteClassification =
                        notificationById?.u_incident_classification?.value

                    const noMandatoryClassification =
                        isSevereOrDeathDamage(notificationById.damage_level.value) ||
                        notificationById.u_image_risk?.value === 'u_image_risk_yes'

                    if (noMandatoryClassification && !incidenteClassification) {
                        setIsMissingForwardToFinish(true)
                    } else if (
                        incidenteClassification ===
                        'u_incident_classification_external' &&
                        !notificationById.u_certifier_accreditor_protocol?.value
                    ) {
                        setIsMissingAnvisaNumber(true)
                    } else {
                        setFinishModal(true)
                    }
                }
            } else
                if (type === 'forward') {
                    setPendingModalText({
                        subtitle: `As notificações Graves, Óbitos ou com Risco de Imagem devem ser finalizadas apenas
                    pelo time da Qualidade da Unidade. 
                    Para encaminhar a notificação para finalização, certifique-se de que todos os dados obrigatórios foram preenchidos.`
                    })
                    setFinishModalData({
                        title: 'Encaminhar notificação para finalização',
                        subtitle: `As notificações Graves, Óbitos ou com Risco de Imagem devem ser finalizadas apenas
                    pelo time da Qualidade da Unidade.
                    Ao encaminhar para finalização, não será mais possível editar as informações de seu tratamento, e essa ação não poderá ser desfeita.`,
                        submitButtonText: 'Encaminhar para finalização.',
                        type: 'forward'
                    })
                    if (isOlder ? notDoneOlder : (notDoneNew != undefined)) {
                        setPendingStepModal(true)
                    } else {
                        setFinishModal(true) //Exibe o modal de finalização da notificação
                    }
                }
        }

        // Executar validação primeiro (se aplicável) e depois prosseguir
        if (currentStepSelected === 1) {
            validateFormAndContinue().then((isValid) => {
                if (isValid) {
                    proceedWithFinish()
                }
            })
        } else {
            proceedWithFinish()
        }
    }
    /**
     * Executa service para mudar o status para finalizada depois de confirmado no modal
     * @returns
     */
    function submitFinished() {
        if (finishModalData.type === 'finish') {
            const dataWithDisclosure = {
                ...notificationSelector?.notificationById,
                u_disclosure: {
                    value: disclosure.u_disclosure,
                    display_value: disclosure.u_disclosure
                }
            }
            if (
                !canFinish(
                    dataWithDisclosure,
                    setFinishErrorMessage
                )
            ) {
                return
            }
        }

        NotificationService.finishNotification(
            notificationSelector?.notificationById?.sys_id.value,
            finishModalData.type,
            authenticationSelector?.dataJwt?.sys_id,
            disclosure
        )
            .then((response: any) => {
                setFinishModal(false)
                NotificationActions.getNotificationById(
                    notificationDispatch,
                    id
                )
                const alertText =
                    finishModalData.type === 'finish'
                        ? 'Notificação finalizada com sucesso!'
                        : 'Notificação encaminhada para finalização com sucesso!'
                setTooltipAlert(alertText)
                setTimeout(() => {
                    setTooltipAlert('')
                }, 4000)
            })
            .catch((error) => {
                console.log(error)
            })
    }
    function submitArchive() {
        NotificationActions.getNotificationById(notificationDispatch, id)
        setTooltipAlert('Notificação arquivada com sucesso!')
        setTimeout(() => {
            setTooltipAlert('')
        }, 4000)
    }

    const showActionsButtonStep1 = Object.keys(dirtyFields).length !== 0
    notificationById?.u_state?.value !== notificationStatus[4]?.value &&
        notificationById?.u_state?.value !== notificationStatus[5]?.value

    return (
        <>
            {
                (featureFlags.groupNotifications) && location.pathname.includes('/group-treatment') ?
                    <GroupNotificationTreatmentsSteps
                        currentStepSelected={currentStepSelected}
                        tooltipAlert={tooltipAlert}
                        setTooltipAlert={setTooltipAlert}
                        notificationStatusState={notificationStatusState}
                        notificationStatus={notificationStatus}
                    />
                    :
                    <>
                        <PendingStepModal
                            subtitle={pendingModalText.subtitle}
                            handleGoToStep={handlePendingModal}
                            isFinishedList={finishedSteps}
                            isOpen={pendingStepModal}
                            handleCloseModal={() => setPendingStepModal(false)}
                        />
                        <ProtocolInfoOk
                            protocolInfoOkData={{
                                title: 'Orientação da Notificação pendente',
                                subtitle: `Para finalizar uma notificação, é preciso que a orientação da notificação seja preenchida
                    pela Diretoria da Qualidade Corporativa.`
                            }}
                            boxText={{
                                upperText: 'Notificação Interna ou Externa?',
                                downText: 'Obrigatório'
                            }}
                            notificationById={notificationSelector?.notificationById}
                            isOpen={isMissingForwardToFinish}
                            handleCloseModal={() => setIsMissingForwardToFinish(false)}
                        />
                        <ProtocolInfoOk
                            protocolInfoOkData={{
                                title: 'Número de protocolo pendente',
                                subtitle: `Para finalizar uma notificação externa é necessário preencher o número de protocolo da
                    Anvisa, Certificadora e/ou Instituição Acreditadora.`
                            }}
                            boxText={{
                                upperText: 'Preencher protocolo',
                                downText: 'Obrigatório'
                            }}
                            notificationById={notificationSelector?.notificationById}
                            isOpen={isMissingAnvisaNumber}
                            handleCloseModal={() => setIsMissingAnvisaNumber(false)}
                        />

                        <FinishNotificationModal
                            finishModalData={finishModalData}
                            finishErrorMessage={finishErrorMessage}
                            notificationById={notificationSelector?.notificationById}
                            submitFinished={submitFinished}
                            isOpen={finishModal}
                            handleCloseModal={() => setFinishModal(false)}
                        />

                        <ArchiveNotificationModal
                            submitArchive={submitArchive}
                            isOpen={archiveModal}
                            handleCloseModal={() => setArchiveModal(false)}
                        />

                        <ArchiveWarning
                            submit={submitArchiveWarning}
                            isOpen={archiveWarningModal}
                            handleCloseModal={() => setArchiveWarningModal(false)}
                        />

                        <ReopenNotificationModal
                            handleReopenNotification={submitReopenNotification}
                            isOpen={reopenNotificationModal}
                            handleCloseModal={() => setReopenNotificationModal(false)}
                            notificationStatusState={notificationStatusState}
                        />

                        <ForwardNotificationModal
                            errorMessage={forwordErrorMessage}
                            handleOpenForwardNotification={handleForwardNotification}
                            isOpen={forwardModal}
                            handleCloseModal={() => setForwardModal(false)}
                        />

                        <CanEditWarning
                            submit={handleSaveEdit}
                            isOpen={editWarningModal}
                            handleCloseModal={() => setEditWarningModal(false)}
                        />

                        <AssumeTreatmentModal
                            id={id}
                            setTooltipAlert={setTooltipAlert}
                            notificationStatusState={notificationStatusState}
                            setCurrentStepSelected={setCurrentStepSelected}
                            isOpen={assumeTreamentModal}
                            handleCloseModal={() => setAssumeTreamentModal(false)}
                        />

                        <div className="fixed top-[88px] w-full flex justify-center z-[99999]">
                            <CanNotEditInfo
                                notificationStatusState={notificationStatusState}
                                currentUser={authenticationSelector?.dataJwt}
                                notificationSector={
                                    notificationSelector?.notificationById?.incident_sector
                                        ?.value
                                }
                                notificationLocation={
                                    notificationSelector?.notificationById
                                        ?.location_incident?.value
                                }
                                notificationById={notificationSelector?.notificationById}
                            />
                            <AssumeTreatmentInfo
                                handleAssumeNotification={() => {
                                    setAssumeTreamentModal(true)
                                }}
                                currentUser={authenticationSelector?.dataJwt}
                                notificationSector={
                                    notificationSelector?.notificationById?.incident_sector
                                        ?.value
                                }
                                notificationLocation={
                                    notificationSelector?.notificationById
                                        ?.location_incident?.value
                                }
                                notificationById={notificationSelector?.notificationById}
                            />
                        </div>
                        <NotifyContainer>
                            <NotifyMain>

                                <NotifyContent>
                                    <NotificationTreatmentHeader
                                        handleCancel={handleCancel}
                                        handleSaveEdit={editModalWarning}
                                        showActionsButtonStep1={showActionsButtonStep1}
                                        forwardToFinish={handleFinishNotification}
                                        handleFinishNotification={handleFinishNotification}
                                        currentUser={authenticationSelector?.dataJwt}
                                        notificationStatusState={notificationStatusState}
                                        isDirty={Object.keys(dirtyFields).length !== 0}
                                        currentStepSelected={currentStepSelected}
                                        setCurrentStepSelected={setCurrentStepSelected}
                                        loggedInUserIsResponsible={loggedInUserIsResponsible}
                                        handleArchiveNotification={
                                            notificationStatusState ===
                                                notificationStatus[2].label
                                                ? () => {
                                                    setArchiveWarningModal(true)
                                                }
                                                : handleArchiveNotification
                                        }
                                        handleOpenForwardNotification={
                                            handleOpenForwardNotification
                                        }
                                        handleReopenNotification={handleReopenNotification}
                                        canReopen={
                                            !isNotifiableReopenBlocked(
                                                notificationSelector?.notificationById?.damage_level?.value
                                            ) &&
                                            !isLocationReopenBlocked(
                                                notificationSelector?.notificationById?.location_incident
                                            )
                                        }
                                        handleAssumeNotification={() => {
                                            NotificationActions.setProgressBarNavClicked(notificationDispatch, false)
                                            setAssumeTreamentModal(true)
                                        }}
                                    />
                                    <NotificationTreatmentProgressBarNav
                                        notificationStatusState={notificationStatusState}
                                        setCurrentStepSelected={setCurrentStepSelected}
                                        currentStepSelected={currentStepSelected}
                                        finishedSteps={finishedSteps}
                                    />

                                    {/* Formulário de tratamento de notificação individual ou em grupo*/}
                                    <form className="pb-6 pt-2">

                                        <NotificationsTreatmentsSteps
                                            currentStepSelected={currentStepSelected}
                                            notificationSelectorIsLoading={notificationSelectorIsLoading}
                                            resetField={resetField}
                                            dirtyFields={dirtyFields}
                                            errors={errors}
                                            authenticationSelector={authenticationSelector}
                                            tooltipAlert={tooltipAlert}
                                            watch={watch}
                                            setValue={setValue}
                                            control={control}
                                            register={register}
                                            statusEdit={statusEdit}
                                            notificationById={notificationById}
                                            setFileUploadS3Attached={setFileUploadS3Attached}
                                            setUrlUploadS3AttachedFile={setUrlUploadS3AttachedFile}
                                            setUrlDeleteS3AttachedFile={setUrlDeleteS3AttachedFile}
                                            notificationStatusState={notificationStatusState}
                                            notificationStatus={notificationStatus}
                                            id={id}
                                            setDisclosure={setDisclosure}
                                            disclosureFieldsAutoSave={disclosureFieldsAutoSave}
                                            disclosureError={disclosureError}
                                            managersReviewError={managersReviewError}
                                            showDisclosure={showDisclosure}
                                            loggedInUserIsResponsible={loggedInUserIsResponsible}
                                        />
                                    </form>
                                </NotifyContent>
                            </NotifyMain>
                        </NotifyContainer>
                    </>
            }
        </>
    )
}

export default NotificationTreatment
